import { useContextRoot } from '@/utils/context-root';
import _ from 'lodash';
import {
  WAYFINDING_MENU_TEMPLATE,
  WAYFINDING_MENU_KEYS,
} from '@/data/wayfinding';
import { computed } from 'vue';

interface AccountMenuChildLink {
  title: () => string;
  route: string;
}
interface AccountMenuItem {
  title: () => string;
  route: string;
  children?: AccountMenuChildLink[];
}

/**
 * Composable for handling wayfinding menu visibility and structure
 */
export function useWayfinding() {
  const ctx = useContextRoot();
  const { $store } = ctx.root;

  // User and feature flag state computed properties
  const netNewInternationalUser = computed<boolean>(
    () => $store.getters.netNewInternationalUser,
  );
  const netNewUser = computed<boolean>(() => $store.getters.netNewUser);
  const isCABusiness = computed<boolean>(() => $store.getters.isCABusiness);
  const userHasForceBnplFlag = computed<boolean>(
    () => $store.getters.userHasForceBnplFlag,
  );

  const canAccessPayments = computed<boolean>(() => {
    return (
      userHasForceBnplFlag.value ||
      (!isCABusiness.value && !netNewInternationalUser.value)
    );
  });

  /**
   * Generates account display links based on feature flags
   */
  const accountDisplayLinks = computed<AccountMenuItem>(() => {
    const linkGroup = _.cloneDeep(
      WAYFINDING_MENU_TEMPLATE[WAYFINDING_MENU_KEYS.ACCOUNT],
    ) as AccountMenuItem;

    const linksToExclude: string[] = [];

    linkGroup.children = linkGroup.children?.filter(
      (link) => !linksToExclude.includes(link.route),
    );

    return linkGroup;
  });

  const getFinancingLinks = () => {
    // Users who do not see MCA:
    // no active MCA advances, no MCA spend available and no settled advances
    if (netNewUser.value) {
      return false;
    }

    const linkGroup = _.cloneDeep(
      WAYFINDING_MENU_TEMPLATE[WAYFINDING_MENU_KEYS.FINANCING],
    );

    return linkGroup;
  };

  const getRevenueSourcesLinks = () => {
    if (netNewInternationalUser.value) {
      return false;
    }

    return _.cloneDeep(
      WAYFINDING_MENU_TEMPLATE[WAYFINDING_MENU_KEYS.REVENUE_SOURCES],
    );
  };

  const getDocumentsLinks = () => {
    if (netNewInternationalUser.value) {
      return false;
    }

    return _.cloneDeep(
      WAYFINDING_MENU_TEMPLATE[WAYFINDING_MENU_KEYS.ALL_DOCUMENTS],
    );
  };

  /**
   * Main function to get visible links based on menu section
   */
  const getVisibleLinks = (linkName: string) => {
    switch (linkName) {
      case WAYFINDING_MENU_KEYS.ACCOUNT:
        return accountDisplayLinks.value;
      case WAYFINDING_MENU_KEYS.PAYMENTS: // BNPL
        return WAYFINDING_MENU_TEMPLATE[WAYFINDING_MENU_KEYS.PAYMENTS];
      case WAYFINDING_MENU_KEYS.UPCOMING_PAYMENTS:
        return canAccessPayments.value
          ? WAYFINDING_MENU_TEMPLATE[WAYFINDING_MENU_KEYS.UPCOMING_PAYMENTS]
          : false;
      case WAYFINDING_MENU_KEYS.BUSINESS_PROFILE:
        return canAccessPayments.value
          ? WAYFINDING_MENU_TEMPLATE[WAYFINDING_MENU_KEYS.BUSINESS_PROFILE]
          : false;
      case WAYFINDING_MENU_KEYS.FINANCING: // MCA
        return getFinancingLinks();
      case WAYFINDING_MENU_KEYS.REVENUE_SOURCES:
        return getRevenueSourcesLinks();
      case WAYFINDING_MENU_KEYS.ALL_DOCUMENTS:
        return getDocumentsLinks();
      default:
        return false;
    }
  };

  /**
   * Returns all link groups after applying visibility rules
   */
  const getLinkGroups = computed(() => {
    return Object.keys(WAYFINDING_MENU_TEMPLATE).map((link) =>
      getVisibleLinks(link),
    );
  });

  const mobileDisplayLinks = computed(() => {
    return getLinkGroups.value.filter((linkGroup) => linkGroup);
  });

  const desktopDisplayLinks = computed(() => {
    return getLinkGroups.value.reduce((acc, linkGroup) => {
      if (linkGroup && linkGroup.route) {
        return [...acc, linkGroup];
      }
      return acc;
    }, [] as AccountMenuItem[]);
  });

  return {
    accountDisplayLinks, // used for Account burger menu
    mobileDisplayLinks, // used for left nav (mobile)
    desktopDisplayLinks, // used for left nav (desktop)
  };
}
